/**
 * @file CustomerBlocksSection.tsx
 * @description История блокировок клиента (phase08.5 shadcn).
 */

import { useEffect, useState } from "react";

import { Alert } from "../../../../components/ui/alert";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { SelectField } from "../../../../components/ui/SelectField";
import { Separator } from "../../../../components/ui/separator";
import { Textarea } from "../../../../components/ui/textarea";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import {
  blocks,
  type CustomerBlockDto,
  type CustomerBlockScope,
} from "../../../../lib/api-customers";

interface Props {
  customerId: string;
  canManage: boolean;
  onChanged: () => void;
}

const SCOPE_LABEL: Record<CustomerBlockScope, string> = {
  ORDERS: "Заказы",
  LOGIN: "Логин",
  PAYMENT: "Оплата",
};

/** Область блокировки с пояснением — общий `SelectField` (phase19.31: нативный список запрещён). */
const SCOPE_OPTIONS: { value: CustomerBlockScope; label: string }[] = [
  { value: "ORDERS", label: "Заказы (нельзя оформить новый)" },
  { value: "LOGIN", label: "Вход (нельзя войти в личный кабинет)" },
  { value: "PAYMENT", label: "Оплата (оспаривание платежей, злоупотребления)" },
];

export function CustomerBlocksSection({ customerId, canManage, onChanged }: Props) {
  const [items, setItems] = useState<CustomerBlockDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [unblockTarget, setUnblockTarget] = useState<CustomerBlockDto | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      setItems(await blocks.list(customerId));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const activeBlocks = items.filter((b) => !b.revokedAt);
  const historyBlocks = items.filter((b) => b.revokedAt);

  return (
    <>
      <EditorialCard>
        <EditorialCard.Body>
          <div className="flex items-center justify-between mb-3">
            <div className="label-editorial">Блокировки</div>
            {canManage ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCreating(true)}
                className="text-destructive hover:bg-destructive/10"
              >
                + Заблокировать
              </Button>
            ) : null}
          </div>

          {loading ? (
            <p className="text-sm text-on-surface-muted">Загрузка…</p>
          ) : items.length === 0 ? (
            <Alert severity="success">Блокировок не было.</Alert>
          ) : (
            <div>
              {[...activeBlocks, ...historyBlocks].map((b, idx) => (
                <div key={b.id}>
                  {idx > 0 ? <Separator /> : null}
                  <div
                    className={`flex items-start gap-3 py-3 ${b.revokedAt ? "opacity-60" : ""}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={b.revokedAt ? "default" : "destructive"}>
                          {SCOPE_LABEL[b.scope]}
                        </Badge>
                        {b.revokedAt ? <Badge variant="default">снято</Badge> : null}
                        {b.expiresAt && !b.revokedAt ? (
                          <Badge variant="outline">
                            до {new Date(b.expiresAt).toLocaleDateString("ru-RU")}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-sm text-on-surface mt-1">{b.reason}</p>
                      <p className="text-xs text-on-surface-variant">
                        Заблокирован: {new Date(b.blockedAt).toLocaleString("ru-RU")}
                        {b.revokedAt
                          ? ` · Снято: ${new Date(b.revokedAt).toLocaleString("ru-RU")}`
                          : ""}
                      </p>
                      {b.revokedReason ? (
                        <p className="text-xs text-on-surface-variant">
                          Причина снятия: {b.revokedReason}
                        </p>
                      ) : null}
                    </div>
                    {canManage && !b.revokedAt ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUnblockTarget(b)}
                      >
                        Снять
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </EditorialCard.Body>
      </EditorialCard>

      {creating ? (
        <CreateBlockDialog
          customerId={customerId}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void refresh();
            onChanged();
          }}
        />
      ) : null}
      {unblockTarget ? (
        <UnblockDialog
          block={unblockTarget}
          onClose={() => setUnblockTarget(null)}
          onUnblocked={() => {
            setUnblockTarget(null);
            void refresh();
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

function CreateBlockDialog({
  customerId,
  onClose,
  onCreated,
}: {
  customerId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const notify = useNotify();
  const [scope, setScope] = useState<CustomerBlockScope>("ORDERS");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    if (reason.trim().length < 5) {
      setError("Причина обязательна (минимум 5 символов).");
      return;
    }
    setSaving(true);
    try {
      await blocks.block(customerId, {
        reason: reason.trim(),
        scope,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      notify.success("Клиент заблокирован");
      onCreated();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Не удалось";
      setError(msg);
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Заблокировать клиента</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Alert severity="info">
            Блокировка ограничивает действия клиента. Причина обязательна по ст.426 ГК.
          </Alert>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="b-scope">Область</Label>
            <SelectField
              id="b-scope"
              ariaLabel="Область блокировки"
              value={scope}
              onChange={(value) => setScope(value as CustomerBlockScope)}
              options={SCOPE_OPTIONS}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="b-reason">Причина</Label>
            <Textarea
              id="b-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={2}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="b-expires">Срок (опционально)</Label>
            <Input
              id="b-expires"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            <p className="text-xs text-on-surface-variant">Если не задано — бессрочно</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onSubmit()}
            disabled={saving}
          >
            {saving ? "Блокируем…" : "Заблокировать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnblockDialog({
  block,
  onClose,
  onUnblocked,
}: {
  block: CustomerBlockDto;
  onClose: () => void;
  onUnblocked: () => void;
}) {
  const notify = useNotify();
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    if (!reason.trim()) {
      setError("Укажите причину снятия.");
      return;
    }
    setSaving(true);
    try {
      await blocks.unblock(block.id, reason.trim());
      notify.success("Блокировка снята");
      onUnblocked();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Не удалось";
      setError(msg);
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Снять блокировку</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="u-reason">Причина снятия</Label>
            <Textarea
              id="u-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={saving}>
            {saving ? "Снимаем…" : "Снять блокировку"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
